import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const services = [
  {
    id: 'automotive',
    tag: 'Most Popular',
    title: 'Automotive Window Tinting',
    description:
      'From sedans to luxury SUVs, our automotive window tinting blocks up to 77% of solar energy, eliminates glare, protects interiors, and delivers a sleek factory finish that lasts.',
    features: ['UV Protection 99%+', 'Heat Rejection up to 77%', 'Privacy Enhancement', 'Interior Protection'],
    image:
      'https://public.readdy.ai/ai/img_res/8b9dcca6acc91354127195b454485aad.jpg',
    icon: 'ri-car-line',
  },
  {
    id: 'residential',
    tag: 'Home & Office',
    title: 'Residential Window Tinting',
    description:
      'Cut energy costs by up to 30%, eliminate UV damage to furniture and flooring, and gain privacy — without sacrificing natural light. Perfect for homes and commercial buildings.',
    features: ['Energy Savings 30%+', '99% UV Block', 'Glare Reduction', 'Fade Prevention'],
    image:
      'https://readdy.ai/api/search-image?query=modern%20luxury%20home%20interior%20with%20large%20floor%20to%20ceiling%20windows%20residential%20window%20tinting%20light%20filtering%20natural%20sunlight%20contemporary%20living%20space%20clean%20minimal%20decor&width=900&height=600&seq=svc_res_01&orientation=landscape',
    icon: 'ri-building-2-line',
  },
  {
    id: 'marine',
    tag: 'On the Water',
    title: 'Marine Window Tinting',
    description:
      'Specialized yacht and boat window films engineered for saltwater environments. Reduce glare on the water, protect marine interiors, and enhance comfort on every voyage.',
    features: ['Anti-Glare on Water', 'Salt-Resistant Films', 'Interior Protection', 'Privacy on Deck'],
    image:
      'https://readdy.ai/api/search-image?query=luxury%20yacht%20with%20tinted%20windows%20on%20the%20ocean%20marine%20boat%20window%20film%20professional%20installation%20sleek%20dark%20tinted%20glass%20on%20superyacht%20clear%20blue%20water%20background%20premium%20nautical%20vessel&width=900&height=600&seq=svc_mar_01&orientation=landscape',
    icon: 'ri-ship-line',
  },
  {
    id: 'ppf',
    tag: 'Paint Protection',
    title: 'Paint Protection Film (PPF)',
    description:
      'Invisible armor for your vehicle. Our PPF installations use self-healing technology that protects against stone chips, scratches, and road debris — keeping your paint showroom-perfect.',
    features: ['Self-Healing Technology', 'Scratch Resistance', 'Gloss Enhancement', 'Warranted Coverage'],
    image:
      'https://readdy.ai/api/search-image?query=paint%20protection%20film%20PPF%20being%20applied%20to%20luxury%20car%20hood%20clear%20protective%20film%20installation%20on%20white%20luxury%20vehicle%20automotive%20detailing%20professional%20technician%20clean%20studio%20environment&width=900&height=600&seq=svc_ppf_01&orientation=landscape',
    icon: 'ri-shield-check-line',
  },
  {
    id: 'ceramic',
    tag: 'Ultimate Protection',
    title: 'Ceramic Coatings',
    description:
      'Nano-ceramic coatings that bond to your vehicle\'s surface at a molecular level — delivering hydrophobic protection, stunning gloss, and UV defense that outlasts traditional wax by years.',
    features: ['Hydrophobic Surface', 'UV Defense', 'High-Gloss Finish', 'Multi-Year Protection'],
    image:
      'https://readdy.ai/api/search-image?query=ceramic%20coating%20being%20applied%20to%20luxury%20sports%20car%20ultra%20glossy%20paint%20finish%20ceramic%20pro%20treatment%20red%20luxury%20vehicle%20intense%20reflection%20showroom%20quality%20automotive%20detailing%20professional&width=900&height=600&seq=svc_cer_01&orientation=landscape',
    icon: 'ri-drop-line',
  },
];

export function Services() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="services" className="py-28 bg-[#0D0D0D] relative overflow-hidden">
      {/* BG texture */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: 'radial-gradient(circle, #FF6B35 1px, transparent 1px)', backgroundSize: '32px 32px' }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10" ref={ref}>
        {/* Header */}
        <div className={`anim-hidden ${isVisible ? 'animate-fade-in-up' : ''} text-center mb-20`}>
          <span className="luxury-divider mx-auto" />
          <span className="text-xs font-bold uppercase tracking-[5px] text-[#C9A96E] mb-4 block">
            What We Do
          </span>
          <h2
            className="text-4xl sm:text-5xl font-bold text-white leading-tight tracking-tight"
            style={{ fontFamily: 'Sora, sans-serif' }}
          >
            Precision Services for
            <br />
            <span className="shimmer-accent">Every Surface</span>
          </h2>
        </div>

        {/* Large featured service first (automotive) */}
        <div
          className={`anim-hidden ${isVisible ? 'animate-fade-in-up delay-100' : ''} group relative rounded-3xl overflow-hidden mb-6 border border-white/5 hover:border-[#FF6B35]/30 transition-all duration-500 cursor-default`}
          data-product-shop
        >
          <div className="relative h-[480px] sm:h-[520px]">
            <img
              src={services[0].image}
              alt={services[0].title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D] via-[#0D0D0D]/60 to-transparent" />
          </div>
          <div className="absolute inset-0 flex flex-col justify-end p-10 lg:p-14">
            <span className="inline-block bg-[#FF6B35] text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4 w-fit">
              {services[0].tag}
            </span>
            <h3
              className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              {services[0].title}
            </h3>
            <p className="text-white/70 text-base mb-6 max-w-lg leading-relaxed">
              {services[0].description}
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              {services[0].features.map((feat) => (
                <span key={feat} className="flex items-center gap-1.5 bg-white/10 text-white/80 text-xs px-3 py-1.5 rounded-full">
                  <i className="ri-check-line text-[#FF6B35]" />
                  {feat}
                </span>
              ))}
            </div>
            <a
              href="#quote"
              className="inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#E55A2B] text-white px-7 py-3.5 rounded-full text-sm font-bold transition-all hover:scale-105 w-fit whitespace-nowrap cursor-pointer"
            >
              Get a Quote <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>

        {/* 2x2 grid for remaining services */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-product-shop>
          {services.slice(1).map((svc, i) => (
            <div
              key={svc.id}
              className={`anim-hidden ${isVisible ? `animate-fade-in-up delay-${(i + 2) * 100}` : ''} group relative rounded-2xl overflow-hidden border border-white/5 hover:border-[#FF6B35]/30 transition-all duration-500 cursor-default`}
            >
              <div className="relative h-64">
                <img
                  src={svc.image}
                  alt={svc.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/40 to-transparent" />
              </div>
              <div className="absolute inset-0 flex flex-col justify-end p-7">
                <span className="text-[#FF6B35] text-xs font-bold uppercase tracking-widest mb-2">
                  {svc.tag}
                </span>
                <h3
                  className="text-xl font-bold text-white mb-2 tracking-tight"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                >
                  {svc.title}
                </h3>
                <p className="text-white/60 text-sm mb-4 leading-relaxed line-clamp-2">
                  {svc.description}
                </p>
                <a
                  href="#quote"
                  className="inline-flex items-center gap-1.5 text-[#FF6B35] text-sm font-semibold hover:gap-3 transition-all cursor-pointer"
                >
                  Get Quote <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
